import {Injectable, signal} from "@angular/core";
import {HealthErrorCodes} from "@models";
import {Translations} from "@translations";

export class AudioHealthResult {
	private readonly errorMap = new Map<number, string>([
		[HealthErrorCodes.AudiodeviceNotRunning, Translations.WEB_RECORDER_AUDIO_DEVICE_NOT_RUNNING],
		[HealthErrorCodes.HttpApiHostNotRunning, Translations.WEB_RECORDER_AUDIO_SERVER_API_NOT_RUNNING],
		[HealthErrorCodes.WebsocketServerNotRunning, Translations.WEB_RECORDER_AUDIO_SERVER_WEBSOCKET_NOT_RUNNING],
		[HealthErrorCodes.DataFolderNotFound, Translations.WEB_RECORDER_AUDIO_SERVER_DATA_FOLDER_NOT_FOUND],
		[HealthErrorCodes.NotReachable, Translations.WEB_RECORDER_AUDIO_SERVER_NOT_REACHABLE],
		[HealthErrorCodes.InvalidChannelMapping, Translations.WEB_RECORDER_AUDIO_SERVER_INVALID_CHANNEL_MAPPING],
		[HealthErrorCodes.LowDiskSpace, Translations.WEB_RECORDER_AUDIO_SERVER_LOW_DISK_SPACE],
		[
			HealthErrorCodes.InvalidChannelMapping_Channel_Number_Exceeds_Audio_Meter,
			Translations.WEB_RECORDER_AUDIO_SERVER_CHANNEL_NUMBER_EXCEEDS_AUDIO_METER,
		],
		[
			HealthErrorCodes.InvalidChannelMapping_Channel_Number_Exceeds_Set_Limit,
			Translations.WEB_RECORDER_AUDIO_SERVER_CHANNEL_NUMBER_EXCEEDS_SET_LIMIT,
		],
		[
			HealthErrorCodes.InvalidChannelMapping_Channel_Number_Less_Than_Zero,
			Translations.WEB_RECORDER_AUDIO_SERVER_CHANNEL_NUMBER_LESS_THAN_ZERO,
		],
		[HealthErrorCodes.AsteriskAudioSocketNotRunning, Translations.WEB_RECORDER_AUDIO_SERVER_ASTERISK_AUDIO_SOCKET_SERVER_NOT_AVAILABLE],
		[
			HealthErrorCodes.InvalidChannelMapping_Duplicate_Input_Channels,
			Translations.WEB_RECORDER_AUDIO_SERVER_INVALID_CHANNEL_MAPPING_DUPLICATE_INPUT_CHANNELS,
		],
		[
			HealthErrorCodes.InvalidChannelMapping_Duplicate_Output_Channels,
			Translations.WEB_RECORDER_AUDIO_SERVER_INVALID_CHANNEL_MAPPING_DUPLICATE_OUTPUT_CHANNELS,
		],
		[
			HealthErrorCodes.InvalidChannelMapping_Duplicate_Phone_Channels,
			Translations.WEB_RECORDER_AUDIO_SERVER_INVALID_CHANNEL_MAPPING_DUPLICATE_PHONE_CHANNELS,
		],
		[
			HealthErrorCodes.TesiraInsufficientDynamicAudioInputChannels,
			Translations.WEB_RECORDER_AUDIO_SERVER_INSUFFICIENT_DYNAMIC_AUDIO_INPUT_CHANNELS,
		],
		[
			HealthErrorCodes.TesiraInsufficientDynamicAudioOutputChannels,
			Translations.WEB_RECORDER_AUDIO_SERVER_INSUFFICIENT_DYNAMIC_AUDIO_OUTPUT_CHANNELS,
		],
	]);

	public get isHealthy(): boolean {
		return this.#criticalErrorCodes.length === 0 && !this.#errorMessage;
	}

	public get hasAnyErrors(): boolean {
		return this.#criticalErrorCodes.length > 0 || this.#warningErrorCodes.length > 0 || !!this.#errorMessage;
	}

	#errorMessage: string | null = null;
	#criticalErrorCodes: HealthErrorCodes[] = [];
	#warningErrorCodes: HealthErrorCodes[] = [];

	public get errorMessage(): string | null {
		if (this.#errorMessage) return this.#errorMessage;

		let ret = this.errorMap.get(this.#criticalErrorCodes?.[0]);
		if (ret) return ret;

		ret = this.errorMap.get(this.#warningErrorCodes?.[0]);
		if (ret) return ret;

		if (this.#criticalErrorCodes?.[0]) {
			return HealthErrorCodes[this.#criticalErrorCodes?.[0]];
		}

		if (this.#warningErrorCodes?.[0]) {
			return HealthErrorCodes[this.#warningErrorCodes?.[0]];
		}

		return null;
	}

	public set errorMessage(value: string | null) {
		this.#errorMessage = value;
	}

	public setErrorCodes(criticalErrorCodes: HealthErrorCodes[], warningErrorCodes: HealthErrorCodes[]) {
		this.#criticalErrorCodes = criticalErrorCodes ?? [];
		this.#warningErrorCodes = warningErrorCodes ?? [];
	}
}

@Injectable({
	providedIn: "root",
})
export class SimSharedAudioDataService {
	public readonly audioHealth = signal<AudioHealthResult>(new AudioHealthResult());
	public readonly volumeData = signal<{[key: string]: number}>({});
}
